import React, {useCallback, useEffect, useRef} from "react";
import {observer} from 'mobx-react-lite';
import {usePaywallStoreContext} from "./Store";
import {RegistrationWall} from "./RegistrationWall";
import {SubscriptionWall} from "./SubscriptionWall";
import {ReadArticle} from "./ReadArticle";
import axios from "axios";
import {analytics} from "anvil-analytics/analytics";
import ReactDOM from "react-dom";
import $ from 'jquery';


/**
 * HOC that controls the paywall prompt.
 *
 * Until all views are used the Read prompt should be shown.
 * Then show the Registration Prompt if enabled.
 * Otherwise show a subscribe prompt.
 *
 * @param {Object} kwargs
 * @param kwargs.pagePk: {number} - Primary key of the page being blocked.
 * @param kwargs.pageAjaxUrl: {string} - URL to ajax in for the full version of the article
 * @param kwargs.pageUrl: {string} - Absolute URL for the blocked article.
 * @type {React.ReactElement}
 */
export const Paywall = observer(function Paywall({pagePk, pageAjaxUrl, pageUrl, blockRegwall, termsOfUseUrl, privacyPolicyUrl}) {
    const paywallStore = usePaywallStoreContext();
    const paywallElement = useRef(null);

    // Function that requests an article and loads it
    // into the DOM.
    const loadArticle = useCallback(async (loadArticleUrl) => {
        // Get the full article
        const response = await axios.get(loadArticleUrl);

        let reactContainer = null;
        if (paywallElement.current !== null)
            reactContainer = paywallElement.current.parentNode;

        // Load in full article html
        const newContent = $(response.data)
            .filter('[data-ajax-content-page-boundary]')
            .children();
        $(paywallElement.current)
            .closest('[data-ajax-content-page-boundary]')
            .html(newContent);

        // Unmount component so that react can perform cleanup
        ReactDOM.unmountComponentAtNode(reactContainer);
    }, [paywallElement]);

    // Setup event event listener for when a user purchases a subscription,
    // so that the paywall is removed when they finish the order tunnel.
    useEffect(() => {
        const subscriptionEventHandler = (event, selectedPagePk) => {
            let url = new URL(pageAjaxUrl);

            if (selectedPagePk !== null && selectedPagePk !== pagePk) {
                if (url.searchParams.has('create_license')) {
                    url.searchParams.delete('create_license');
                }
            }
            loadArticle(url.toString());
        };
        $(document).on('subscription', subscriptionEventHandler);

        return () => $(document).off('subscription', subscriptionEventHandler);
    }, [loadArticle, pageAjaxUrl, pagePk]);

    // Handle "Read Article" button clicks
    const readArticleHandler = useCallback(async () => {
        await loadArticle(pageAjaxUrl);

        // Decrement the number of views remaining.
        paywallStore.views.remaining--;

        // Send an analytics event for the article being revealed.
        analytics.track('RevealArticle', {
            page: pageUrl,
            views_remaining: paywallStore.views.remaining
        });
    }, [loadArticle, pageAjaxUrl, paywallStore.views.remaining, pageUrl]);

    // NOTE: This component will not be rendered if the user has access.
    // The following cases cover the differing paywall prompts for users who do not have access.

    if (paywallStore.views.remaining > 0 && !blockRegwall) {
        return (
            <div ref={paywallElement}>
                <ReadArticle pageUrl={pageUrl} readArticleHandler={readArticleHandler}/>
            </div>
        );
    }

    if (paywallStore.registrationWall.enabled && !blockRegwall) {
        return (
            <div ref={paywallElement}>
                <RegistrationWall pagePk={pagePk} pageUrl={pageUrl} termsOfUseUrl={termsOfUseUrl} privacyPolicyUrl={privacyPolicyUrl}/>
            </div>
        );
    }

    let showSubscribeButton = true;
    if (paywallStore.views.remaining === 0 && paywallStore.subscription.userHasOnlineCirculationSubscription) {
        showSubscribeButton = false;
    }

    return (
        <div ref={paywallElement}>
            <SubscriptionWall pageUrl={pageUrl} showButton={showSubscribeButton}/>
        </div>
    )
});

